import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Plus,
  Heart,
  Sparkles,
  BookOpen,
  PartyPopper,
  UsersRound,
  Check,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, isPast } from "date-fns";

const CATEGORIES = [
  { id: "social", label: "Social", icon: UsersRound, color: "text-blue-500" },
  { id: "wellness", label: "Wellness", icon: Heart, color: "text-pink-500" },
  { id: "learning", label: "Learning", icon: BookOpen, color: "text-amber-500" },
  { id: "celebration", label: "Celebration", icon: PartyPopper, color: "text-purple-500" },
  { id: "community", label: "Community", icon: Users, color: "text-green-500" },
];

const getCategoryInfo = (categoryId) => {
  return CATEGORIES.find((c) => c.id === categoryId) || CATEGORIES[0];
};

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [myRsvps, setMyRsvps] = useState({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    location: "",
    event_date: "",
    event_time: "",
    max_attendees: "",
    cover_image: "",
    category: "social",
  });

  useEffect(() => {
    fetchEvents();
  }, [selectedCategory]);

  const fetchEvents = async () => {
    try {
      const params = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
      const response = await axios.get(`${API}/events${params}`);
      setEvents(response.data);
      
      // Fetch RSVP status for each event
      const rsvpStatuses = {};
      for (const event of response.data) {
        try {
          const rsvpResponse = await axios.get(`${API}/events/${event.id}/rsvp-status`);
          rsvpStatuses[event.id] = rsvpResponse.data.status;
        } catch (e) {
          rsvpStatuses[event.id] = "not_going";
        }
      }
      setMyRsvps(rsvpStatuses);
    } catch (error) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        ...newEvent,
        max_attendees: newEvent.max_attendees ? parseInt(newEvent.max_attendees) : null,
      };
      const response = await axios.post(`${API}/events`, eventData);
      setEvents((prev) => [response.data, ...prev]);
      setCreateOpen(false);
      setNewEvent({
        title: "",
        description: "",
        location: "",
        event_date: "",
        event_time: "",
        max_attendees: "",
        cover_image: "",
        category: "social",
      });
      toast.success("Event created! Let's spread the positivity! 🎉");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create event");
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      await axios.post(`${API}/events/${eventId}/rsvp`, { status });
      setMyRsvps((prev) => ({ ...prev, [eventId]: status }));
      
      // Update attendee count locally
      setEvents((prev) =>
        prev.map((e) => {
          if (e.id !== eventId) return e;
          const oldStatus = myRsvps[eventId];
          let countChange = 0;
          if (oldStatus === "going" && status !== "going") countChange = -1;
          if (oldStatus !== "going" && status === "going") countChange = 1;
          return { ...e, attendees_count: e.attendees_count + countChange };
        })
      );
      
      if (status === "going") {
        toast.success("You're going! See you there! 🙌");
      } else if (status === "interested") {
        toast.success("Marked as interested!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to RSVP");
    }
  };

  const upcomingEvents = events.filter((e) => !isPast(parseISO(e.event_date)));
  const pastEvents = events.filter((e) => isPast(parseISO(e.event_date)));

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/10 to-primary/10"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Events
            </h1>
            <p className="text-muted-foreground text-lg">
              Join positive gatherings and make meaningful connections
            </p>
          </div>

          {/* Create button */}
          <div className="flex justify-center">
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-event-btn" className="btn-primary">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Event
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Event</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateEvent} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={newEvent.category}
                      onValueChange={(value) =>
                        setNewEvent((prev) => ({ ...prev, category: value }))
                      }
                    >
                      <SelectTrigger data-testid="event-category-select" className="input-happy">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => {
                          const Icon = cat.icon;
                          return (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <Icon className={`w-4 h-4 ${cat.color}`} />
                                <span>{cat.label}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Event Title</Label>
                    <Input
                      data-testid="event-title-input"
                      placeholder="Give your event a catchy name"
                      value={newEvent.title}
                      onChange={(e) =>
                        setNewEvent((prev) => ({ ...prev, title: e.target.value }))
                      }
                      required
                      className="input-happy"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      data-testid="event-description-input"
                      placeholder="What's this event about?"
                      value={newEvent.description}
                      onChange={(e) =>
                        setNewEvent((prev) => ({ ...prev, description: e.target.value }))
                      }
                      required
                      className="input-happy min-h-[100px]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        data-testid="event-date-input"
                        value={newEvent.event_date}
                        onChange={(e) =>
                          setNewEvent((prev) => ({ ...prev, event_date: e.target.value }))
                        }
                        required
                        className="input-happy"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input
                        type="time"
                        data-testid="event-time-input"
                        value={newEvent.event_time}
                        onChange={(e) =>
                          setNewEvent((prev) => ({ ...prev, event_time: e.target.value }))
                        }
                        required
                        className="input-happy"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      data-testid="event-location-input"
                      placeholder="Where will it take place?"
                      value={newEvent.location}
                      onChange={(e) =>
                        setNewEvent((prev) => ({ ...prev, location: e.target.value }))
                      }
                      required
                      className="input-happy"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Max Attendees (optional)</Label>
                    <Input
                      type="number"
                      data-testid="event-max-attendees-input"
                      placeholder="Leave empty for unlimited"
                      value={newEvent.max_attendees}
                      onChange={(e) =>
                        setNewEvent((prev) => ({ ...prev, max_attendees: e.target.value }))
                      }
                      className="input-happy"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Cover Image URL (optional)</Label>
                    <Input
                      type="url"
                      data-testid="event-cover-input"
                      placeholder="https://..."
                      value={newEvent.cover_image}
                      onChange={(e) =>
                        setNewEvent((prev) => ({ ...prev, cover_image: e.target.value }))
                      }
                      className="input-happy"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="save-event-btn"
                    className="w-full btn-primary"
                  >
                    Create Event
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === "all"
                ? "bg-primary text-white"
                : "bg-muted hover:bg-muted/80"
            }`}
          >
            All Events
          </button>
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.id
                    ? "bg-primary text-white"
                    : "bg-muted hover:bg-muted/80"
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="upcoming" className="space-y-6">
          <TabsList>
            <TabsTrigger value="upcoming">
              Upcoming ({upcomingEvents.length})
            </TabsTrigger>
            <TabsTrigger value="past">Past ({pastEvents.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="card-happy animate-pulse h-64"></div>
                ))}
              </div>
            ) : upcomingEvents.length === 0 ? (
              <div className="card-happy text-center py-16">
                <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No upcoming events</h3>
                <p className="text-muted-foreground mb-6">
                  Be the first to create a positive gathering!
                </p>
                <Button
                  onClick={() => setCreateOpen(true)}
                  className="btn-primary"
                >
                  Create Event
                </Button>
              </div>
            ) : (
              <div
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                data-testid="events-grid"
              >
                {upcomingEvents.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    rsvpStatus={myRsvps[event.id]}
                    onRSVP={handleRSVP}
                    isHost={event.user_id === user?.id}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastEvents.length === 0 ? (
              <div className="card-happy text-center py-12">
                <p className="text-muted-foreground">No past events yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pastEvents.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    rsvpStatus={myRsvps[event.id]}
                    onRSVP={handleRSVP}
                    isPast
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Navigation />
    </div>
  );
}

function EventCard({ event, rsvpStatus, onRSVP, isHost, isPast }) {
  const category = getCategoryInfo(event.category);
  const Icon = category.icon;

  return (
    <div
      className={`card-happy overflow-hidden ${isPast ? "opacity-70" : ""}`}
      data-testid={`event-card-${event.id}`}
    >
      {/* Cover image */}
      {event.cover_image && (
        <div className="h-32 -mx-6 -mt-6 mb-4 overflow-hidden">
          <img
            src={event.cover_image}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Category badge */}
      <div className="flex items-center justify-between mb-3">
        <Badge variant="secondary" className="flex items-center gap-1">
          <Icon className={`w-3 h-3 ${category.color}`} />
          {category.label}
        </Badge>
        {isHost && (
          <Badge className="bg-primary/10 text-primary">Host</Badge>
        )}
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold mb-2">{event.title}</h3>

      {/* Description */}
      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
        {event.description}
      </p>

      {/* Details */}
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>{format(parseISO(event.event_date), "EEEE, MMMM d, yyyy")}</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>{event.event_time}</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{event.location}</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Users className="w-4 h-4" />
          <span>
            {event.attendees_count} going
            {event.max_attendees && ` / ${event.max_attendees} max`}
          </span>
        </div>
      </div>

      {/* RSVP buttons */}
      {!isPast && (
        <div className="flex gap-2">
          <Button
            data-testid={`rsvp-going-${event.id}`}
            onClick={() => onRSVP(event.id, rsvpStatus === "going" ? "not_going" : "going")}
            variant={rsvpStatus === "going" ? "default" : "outline"}
            className={`flex-1 rounded-full ${
              rsvpStatus === "going" ? "bg-primary" : ""
            }`}
          >
            {rsvpStatus === "going" ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Going
              </>
            ) : (
              "I'm Going"
            )}
          </Button>
          <Button
            data-testid={`rsvp-interested-${event.id}`}
            onClick={() =>
              onRSVP(event.id, rsvpStatus === "interested" ? "not_going" : "interested")
            }
            variant={rsvpStatus === "interested" ? "default" : "outline"}
            className={`rounded-full ${
              rsvpStatus === "interested" ? "bg-secondary" : ""
            }`}
          >
            <Star
              className={`w-4 h-4 ${rsvpStatus === "interested" ? "fill-current" : ""}`}
            />
          </Button>
        </div>
      )}

      {/* Host info */}
      <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
        Hosted by <span className="font-medium text-foreground">{event.host_name}</span>
      </div>
    </div>
  );
}
